var __defProp = Object.defineProperty;

var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;

var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

  return value;
};

import * as prismicH from "@prismicio/helpers";
import { appendPredicates } from "./lib/appendPredicates.js";
import { castArray } from "./lib/castArray.js";
import { castThunk } from "./lib/castThunk.js";
import { findMasterRef } from "./lib/findMasterRef.js";
import { findRefByID } from "./lib/findRefByID.js";
import { findRefByLabel } from "./lib/findRefByLabel.js";
import { getPreviewCookie } from "./lib/getPreviewCookie.js";
import { minifyGraphQLQuery } from "./lib/minifyGraphQLQuery.js";
import { ForbiddenError } from "./ForbiddenError.js";
import { NotFoundError } from "./NotFoundError.js";
import { ParsingError } from "./ParsingError.js";
import { PrismicError } from "./PrismicError.js";
import { buildQueryURL } from "./buildQueryURL.js";
import { getRepositoryEndpoint } from "./getRepositoryEndpoint.js";
import { isRepositoryEndpoint } from "./isRepositoryEndpoint.js";
import { predicate } from "./predicate.js";
const MAX_PAGE_SIZE = 100;
const REPOSITORY_CACHE_TTL = 5e3;
const GET_ALL_QUERY_DELAY = 500;
const DEFUALT_RETRY_AFTER_MS = 1e3;
var RefStateMode;

(function (RefStateMode2) {
  RefStateMode2["Master"] = "Master";
  RefStateMode2["ReleaseID"] = "ReleaseID";
  RefStateMode2["ReleaseLabel"] = "ReleaseLabel";
  RefStateMode2["Manual"] = "Manual";
})(RefStateMode || (RefStateMode = {}));

const typePredicate = documentType => predicate.at("document.type", documentType);

const everyTagPredicate = tags => predicate.at("document.tags", castArray(tags));

const someTagsPredicate = tags => predicate.any("document.tags", castArray(tags));

const createClient = (repositoryNameOrEndpoint, options) => new Client(repositoryNameOrEndpoint, options);

class Client {
  constructor(repositoryNameOrEndpoint) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    __publicField(this, "endpoint");

    __publicField(this, "accessToken");

    __publicField(this, "routes");

    __publicField(this, "brokenRoute");

    __publicField(this, "fetchFn");

    __publicField(this, "fetchOptions");

    __publicField(this, "defaultParams");

    __publicField(this, "refState", {
      mode: RefStateMode.Master,
      autoPreviewsEnabled: true
    });

    __publicField(this, "cachedRepository");

    __publicField(this, "cachedRepositoryExpiration", 0);

    __publicField(this, "graphqlFetch", this.graphQLFetch.bind(this));

    if (isRepositoryEndpoint(repositoryNameOrEndpoint)) {
      if (process.env.NODE_ENV === "development" && /\.prismic\.io\/(?!api\/v2\/?)/.test(repositoryNameOrEndpoint)) {
        throw new PrismicError("@prismicio/client only supports Prismic Rest API V2. Please provide only the repository name to the first createClient() parameter or use the getRepositoryEndpoint() helper to generate a valid Rest API V2 endpoint URL.", void 0, void 0);
      }

      this.endpoint = repositoryNameOrEndpoint;
    } else {
      this.endpoint = getRepositoryEndpoint(repositoryNameOrEndpoint);
    }

    this.accessToken = options.accessToken;
    this.routes = options.routes;
    this.brokenRoute = options.brokenRoute;
    this.fetchOptions = options.fetchOptions;
    this.defaultParams = options.defaultParams;

    if (options.ref) {
      this.queryContentFromRef(options.ref);
    }

    if (typeof options.fetch === "function") {
      this.fetchFn = options.fetch;
    } else if (typeof globalThis.fetch === "function") {
      this.fetchFn = globalThis.fetch;
    } else {
      throw new PrismicError("A valid fetch implementation was not provided. In environments where fetch is not available (including Node.js), a fetch implementation must be provided via a polyfill or the `fetch` option.", void 0, void 0);
    }

    if (this.fetchFn === globalThis.fetch) {
      this.fetchFn = this.fetchFn.bind(globalThis);
    }

    this.graphQLFetch = this.graphQLFetch.bind(this);
  }

  enableAutoPreviews() {
    this.refState.autoPreviewsEnabled = true;
  }

  enableAutoPreviewsFromReq(req) {
    this.refState.httpRequest = req;
    this.refState.autoPreviewsEnabled = true;
  }

  disableAutoPreviews() {
    this.refState.autoPreviewsEnabled = false;
  }

  async query(predicates, params) {
    const url = await this.buildQueryURL({ ...params,
      predicates
    });
    return await this.fetch(url, params);
  }

  async get(params) {
    const url = await this.buildQueryURL(params);
    return await this.fetch(url, params);
  }

  async getFirst(params) {
    var _a;

    const actualParams = { ...params
    };

    if (!(params && params.page) && !(params == null ? void 0 : params.pageSize)) {
      var _ref;

      actualParams.pageSize = (_ref = (_a = this.defaultParams) == null ? void 0 : _a.pageSize) !== null && _ref !== void 0 ? _ref : 1;
    }

    const url = await this.buildQueryURL(actualParams);
    const result = await this.fetch(url, params);
    const firstResult = result.results[0];

    if (firstResult) {
      return firstResult;
    }

    throw new PrismicError("No documents were returned", url, void 0);
  }

  async dangerouslyGetAll() {
    let params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var _a;

    const {
      limit = Infinity,
      ...actualParams
    } = params;
    const resolvedParams = { ...actualParams,
      pageSize: Math.min(limit, actualParams.pageSize || ((_a = this.defaultParams) == null ? void 0 : _a.pageSize) || MAX_PAGE_SIZE)
    };
    const documents = [];
    let latestResult;

    while ((!latestResult || latestResult.next_page) && documents.length < limit) {
      const page = latestResult ? latestResult.page + 1 : void 0;
      latestResult = await this.get({ ...resolvedParams,
        page
      });
      documents.push(...latestResult.results);

      if (latestResult.next_page) {
        await new Promise(res => setTimeout(res, GET_ALL_QUERY_DELAY));
      }
    }

    return documents.slice(0, limit);
  }

  async getByID(id, params) {
    return await this.getFirst(appendPredicates(params, predicate.at("document.id", id)));
  }

  async getByIDs(ids, params) {
    return await this.get(appendPredicates(params, predicate.in("document.id", ids)));
  }

  async getAllByIDs(ids, params) {
    return await this.dangerouslyGetAll(appendPredicates(params, predicate.in("document.id", ids)));
  }

  async getByUID(documentType, uid, params) {
    return await this.getFirst(appendPredicates(params, [typePredicate(documentType), predicate.at("my.".concat(documentType, ".uid"), uid)]));
  }

  async getByUIDs(documentType, uids, params) {
    return await this.get(appendPredicates(params, [typePredicate(documentType), predicate.in("my.".concat(documentType, ".uid"), uids)]));
  }

  async getAllByUIDs(documentType, uids, params) {
    return await this.dangerouslyGetAll(appendPredicates(params, [typePredicate(documentType), predicate.in("my.".concat(documentType, ".uid"), uids)]));
  }

  async getSingle(documentType, params) {
    return await this.getFirst(appendPredicates(params, typePredicate(documentType)));
  }

  async getByType(documentType, params) {
    return await this.get(appendPredicates(params, typePredicate(documentType)));
  }

  async getAllByType(documentType, params) {
    return await this.dangerouslyGetAll(appendPredicates(params, typePredicate(documentType)));
  }

  async getByTag(tag, params) {
    return await this.get(appendPredicates(params, someTagsPredicate(tag)));
  }

  async getAllByTag(tag, params) {
    return await this.dangerouslyGetAll(appendPredicates(params, someTagsPredicate(tag)));
  }

  async getByEveryTag(tags, params) {
    return await this.get(appendPredicates(params, everyTagPredicate(tags)));
  }

  async getAllByEveryTag(tags, params) {
    return await this.dangerouslyGetAll(appendPredicates(params, everyTagPredicate(tags)));
  }

  async getBySomeTags(tags, params) {
    return await this.get(appendPredicates(params, someTagsPredicate(tags)));
  }

  async getAllBySomeTags(tags, params) {
    return await this.dangerouslyGetAll(appendPredicates(params, someTagsPredicate(tags)));
  }

  async getRepository(params) {
    const url = new URL(this.endpoint);

    if (this.accessToken) {
      url.searchParams.set("access_token", this.accessToken);
    }

    return await this.fetch(url.toString(), params);
  }

  async getRefs(params) {
    const repository = await this.getRepository(params);
    return repository.refs;
  }

  async getRefByID(id, params) {
    const refs = await this.getRefs(params);
    return findRefByID(refs, id);
  }

  async getRefByLabel(label, params) {
    const refs = await this.getRefs(params);
    return findRefByLabel(refs, label);
  }

  async getMasterRef(params) {
    const refs = await this.getRefs(params);
    return findMasterRef(refs);
  }

  async getReleases(params) {
    const refs = await this.getRefs(params);
    return refs.filter(ref => !ref.isMasterRef);
  }

  async getReleaseByID(id, params) {
    const releases = await this.getReleases(params);
    return findRefByID(releases, id);
  }

  async getReleaseByLabel(label, params) {
    const releases = await this.getReleases(params);
    return findRefByLabel(releases, label);
  }

  async getTags(params) {
    try {
      const tagsForm = await this.getCachedRepositoryForm("tags", params);
      const url = new URL(tagsForm.action);

      if (this.accessToken) {
        url.searchParams.set("access_token", this.accessToken);
      }

      return await this.fetch(url.toString(), params);
    } catch {
      const repository = await this.getRepository(params);
      return repository.tags;
    }
  }

  async buildQueryURL() {
    let {
      signal,
      fetchOptions,
      ...params
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const ref = params.ref || (await this.getResolvedRefString({
      signal,
      fetchOptions
    }));
    const integrationFieldsRef = params.integrationFieldsRef || (await this.getCachedRepository({
      signal,
      fetchOptions
    })).integrationFieldsRef || void 0;
    return buildQueryURL(this.endpoint, { ...this.defaultParams,
      ...params,
      ref,
      integrationFieldsRef,
      routes: params.routes || this.routes,
      brokenRoute: params.brokenRoute || this.brokenRoute,
      accessToken: params.accessToken || this.accessToken
    });
  }

  async resolvePreviewURL(args) {
    var _a, _b;

    let documentID = args.documentID;
    let previewToken = args.previewToken;

    if (typeof globalThis.location !== "undefined") {
      const searchParams = new URLSearchParams(globalThis.location.search);
      documentID = documentID || searchParams.get("documentId");
      previewToken = previewToken || searchParams.get("token");
    } else if (this.refState.httpRequest) {
      if ("query" in this.refState.httpRequest) {
        documentID = documentID || ((_a = this.refState.httpRequest.query) == null ? void 0 : _a.documentId);
        previewToken = previewToken || ((_b = this.refState.httpRequest.query) == null ? void 0 : _b.token);
      } else if ("url" in this.refState.httpRequest && this.refState.httpRequest.url) {
        const searchParams = new URL(this.refState.httpRequest.url).searchParams;
        documentID = documentID || searchParams.get("documentId");
        previewToken = previewToken || searchParams.get("token");
      }
    }

    if (documentID != null && previewToken != null) {
      const document = await this.getByID(documentID, {
        ref: previewToken,
        lang: "*",
        signal: args.signal,
        fetchOptions: args.fetchOptions
      });
      const url = prismicH.asLink(document, args.linkResolver);

      if (typeof url === "string") {
        return url;
      }
    }

    return args.defaultURL;
  }

  queryLatestContent() {
    this.refState.mode = RefStateMode.Master;
  }

  queryContentFromReleaseByID(releaseID) {
    this.refState = { ...this.refState,
      mode: RefStateMode.ReleaseID,
      releaseID
    };
  }

  queryContentFromReleaseByLabel(releaseLabel) {
    this.refState = { ...this.refState,
      mode: RefStateMode.ReleaseLabel,
      releaseLabel
    };
  }

  queryContentFromRef(ref) {
    this.refState = { ...this.refState,
      mode: RefStateMode.Manual,
      ref
    };
  }

  async graphQLFetch(input, init) {
    const cachedRepository = await this.getCachedRepository();
    const ref = await this.getResolvedRefString();
    const unsanitizedHeaders = {
      "Prismic-ref": ref,
      Authorization: this.accessToken ? "Token ".concat(this.accessToken) : "",
      ...(init ? init.headers : {})
    };

    if (cachedRepository.integrationFieldsRef) {
      unsanitizedHeaders["Prismic-integration-field-ref"] = cachedRepository.integrationFieldsRef;
    }

    const headers = {};

    for (const key in unsanitizedHeaders) {
      if (unsanitizedHeaders[key]) {
        headers[key.toLowerCase()] = unsanitizedHeaders[key];
      }
    }

    const url = new URL(input);
    url.searchParams.set("ref", ref);
    const query = url.searchParams.get("query");

    if (query) {
      url.searchParams.set("query", minifyGraphQLQuery(query));
    }

    return await this.fetchFn(url.toString(), { ...init,
      headers
    });
  }

  async getCachedRepository(params) {
    if (!this.cachedRepository || Date.now() >= this.cachedRepositoryExpiration) {
      this.cachedRepositoryExpiration = Date.now() + REPOSITORY_CACHE_TTL;
      this.cachedRepository = await this.getRepository(params);
    }

    return this.cachedRepository;
  }

  async getCachedRepositoryForm(name, params) {
    const cachedRepository = await this.getCachedRepository(params);
    const form = cachedRepository.forms[name];

    if (!form) {
      throw new PrismicError("Form with name \"".concat(name, "\" could not be found"), void 0, void 0);
    }

    return form;
  }

  async getResolvedRefString(params) {
    var _a, _b;

    if (this.refState.autoPreviewsEnabled) {
      let previewRef;
      let cookieJar;

      if ((_a = this.refState.httpRequest) == null ? void 0 : _a.headers) {
        if ("get" in this.refState.httpRequest.headers && typeof this.refState.httpRequest.headers.get === "function") {
          cookieJar = this.refState.httpRequest.headers.get("cookie");
        } else if ("cookie" in this.refState.httpRequest.headers) {
          cookieJar = this.refState.httpRequest.headers.cookie;
        }
      } else if ((_b = globalThis.document) == null ? void 0 : _b.cookie) {
        cookieJar = globalThis.document.cookie;
      }

      if (cookieJar) {
        previewRef = getPreviewCookie(cookieJar);
      }

      if (previewRef) {
        return previewRef;
      }
    }

    const cachedRepository = await this.getCachedRepository(params);
    const refModeType = this.refState.mode;

    if (refModeType === RefStateMode.ReleaseID) {
      return findRefByID(cachedRepository.refs, this.refState.releaseID).ref;
    } else if (refModeType === RefStateMode.ReleaseLabel) {
      return findRefByLabel(cachedRepository.refs, this.refState.releaseLabel).ref;
    } else if (refModeType === RefStateMode.Manual) {
      const res = await castThunk(this.refState.ref)();

      if (typeof res === "string") {
        return res;
      }
    }

    return findMasterRef(cachedRepository.refs).ref;
  }

  async fetch(url) {
    let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var _a, _b, _c, _d;

    const res = await this.fetchFn(url, { ...this.fetchOptions,
      ...params.fetchOptions,
      headers: { ...((_a = this.fetchOptions) == null ? void 0 : _a.headers),
        ...((_b = params.fetchOptions) == null ? void 0 : _b.headers)
      },
      signal: ((_c = params.fetchOptions) == null ? void 0 : _c.signal) || params.signal || ((_d = this.fetchOptions) == null ? void 0 : _d.signal)
    });
    let json;

    try {
      json = await res.json();
    } catch {
      if (res.status === 404) {
        throw new NotFoundError("Prismic repository not found. Check that \"".concat(this.endpoint, "\" is pointing to the correct repository."), url, void 0);
      } else {
        throw new PrismicError(void 0, url, void 0);
      }
    }

    switch (res.status) {
      case 200:
        {
          return json;
        }

      case 400:
        {
          throw new ParsingError(json.message, url, json);
        }

      case 401:
      case 403:
        {
          throw new ForbiddenError("error" in json ? json.error : json.message, url, json);
        }

      case 429:
        {
          const parsedRetryAfter = Number(res.headers.get("retry-after"));
          const delay = Number.isNaN(parsedRetryAfter) ? DEFUALT_RETRY_AFTER_MS : parsedRetryAfter;
          return await new Promise((resolve, reject) => {
            setTimeout(async () => {
              try {
                resolve(await this.fetch(url, params));
              } catch (error) {
                reject(error);
              }
            }, delay);
          });
        }
    }

    throw new PrismicError(void 0, url, json);
  }

}

export { Client, GET_ALL_QUERY_DELAY, REPOSITORY_CACHE_TTL, createClient };